import { DealPageHeader } from "@/components/deal-page-header";
import { DealNav } from "@/components/deal-nav";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getDealDetail } from "@/lib/deals.functions";
import { getModelReviewForDeal } from "@/lib/model-review.functions";
import { useAuth } from "@/hooks/use-auth";

export const Route = createFileRoute(
  "/_authed/deals/$dealId_/model-review/$reportId",
)({
  head: () => ({
    meta: [
      { title: "Financial Model Review: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: ModelReviewReportPage,
});

/* ---------------------------------------------------------------- *
 * Defensive readers. The report JSON is model generated, so every
 * field is treated as possibly missing or the wrong type.
 * ---------------------------------------------------------------- */

type Dict = Record<string, unknown>;

function asDict(v: unknown): Dict | null {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Dict) : null;
}
function asArray(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}
function asStr(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v : null;
}
function asNum(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

const GRADE_CLASS: Record<string, string> = {
  Strong: "bg-emerald-100 text-emerald-900",
  Adequate: "bg-muted text-foreground",
  Weak: "bg-amber-100 text-amber-900",
  "Not Applicable": "bg-muted text-muted-foreground",
};

const CLASSIFICATION_CLASS: Record<string, string> = {
  Conservative: "bg-emerald-100 text-emerald-900",
  Reasonable: "bg-muted text-foreground",
  Aggressive: "bg-amber-100 text-amber-900",
  Unsupported: "bg-red-100 text-red-900",
};

const PRIORITY_ORDER: Record<string, number> = { High: 0, Medium: 1, Low: 2 };

function Badge({ label, className }: { label: string; className: string }) {
  return (
    <span
      className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${className}`}
    >
      {label}
    </span>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-brand-line bg-brand-paper p-5">
      <h2 className="font-serif text-lg text-brand-ink">{title}</h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function ModelReviewReportPage() {
  const { dealId, reportId } = Route.useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const fetchReport = useServerFn(getModelReviewForDeal);
  const fetchDeal = useServerFn(getDealDetail);

  const reportQuery = useQuery({
    queryKey: ["model-review-report", dealId, reportId],
    queryFn: () => fetchReport({ data: { dealId, reportId } }),
    enabled: !!user,
  });

  const dealQuery = useQuery({
    queryKey: ["deal-detail", dealId],
    queryFn: () => fetchDeal({ data: { dealId } }),
    enabled: !!user,
  });

  const row = (reportQuery.data?.report ?? null) as Dict | null;
  const status = asStr(row?.report_status);

  // A report still in flight belongs on the deal page, where the status
  // block and elapsed timer live.
  useEffect(() => {
    if (status === "processing") {
      navigate({ to: "/deals/$dealId", params: { dealId } });
    }
  }, [status, dealId, navigate]);

  // Scope print styling to this page only.
  useEffect(() => {
    document.body.classList.add("model-review-print");
    return () => {
      document.body.classList.remove("model-review-print");
    };
  }, []);

  if (reportQuery.isLoading || dealQuery.isLoading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-10 text-sm text-muted-foreground">
        Loading the report...
      </div>
    );
  }

  if (reportQuery.isError || !row) {
    return (
      <div className="mx-auto max-w-4xl space-y-3 px-4 py-10">
        <p className="text-sm text-brand-ink">This report is not available.</p>
        <Link
          to="/deals/$dealId"
          params={{ dealId }}
          className="text-sm underline underline-offset-2"
        >
          Back to the deal
        </Link>
      </div>
    );
  }

  const dealName =
    asStr((dealQuery.data as { deal?: { name?: string } } | undefined)?.deal?.name) ??
    "Deal";
  const pageTitle = `${dealName} Financial Model Review`;

  const handleDownloadPdf = () => {
    const originalTitle = document.title;
    document.title = pageTitle;
    const restore = () => {
      document.title = originalTitle;
      window.removeEventListener("afterprint", restore);
    };
    window.addEventListener("afterprint", restore);
    setTimeout(() => {
      if (document.title === pageTitle) {
        document.title = originalTitle;
        window.removeEventListener("afterprint", restore);
      }
    }, 1000);
    window.print();
  };

  if (status === "failed") {
    return (
      <div className="mx-auto max-w-4xl space-y-3 px-4 py-10">
        <h1 className="font-serif text-2xl text-brand-ink">{pageTitle}</h1>
        <p className="text-sm text-brand-ink">
          This review did not finish successfully. Any credits charged were
          returned.
        </p>
        <Link
          to="/deals/$dealId"
          params={{ dealId }}
          className="text-sm underline underline-offset-2"
        >
          Back to the deal
        </Link>
      </div>
    );
  }

  if (status === "processing") return null;

  const report = asDict(row.report_json) ?? {};
  const facts = asDict(row.facts_json);
  const summary = asDict(report.executive_summary) ?? {};
  const scan = asDict(report.scan_summary);

  const score = asNum(summary.overall_score) ?? asNum(row.overall_score);
  const recommendation =
    asStr(summary.recommendation) ?? asStr(row.recommendation);
  const confidence = asStr(summary.confidence);
  const confidenceLimiters = asStr(summary.confidence_limiters);

  const matchesBranch = report.matches_deal_branch;
  const modelTypeDetected = asStr(report.model_type_detected);
  const language = asStr(report.language);

  const observations = asArray(summary.top_observations)
    .map(asStr)
    .filter((s): s is string => !!s);
  const modelingRisk = asStr(summary.biggest_modeling_risk);
  const businessRisk = asStr(summary.biggest_business_risk);

  /* Workbook facts: facts_json is the deterministic scan and wins when
   * present. scan_summary is the model's restatement of it. */
  const factSheets = asArray(facts?.sheets)
    .map(asDict)
    .filter((s): s is Dict => !!s);
  const useFacts = !!facts && factSheets.length > 0;

  const sheetsTotal = useFacts
    ? factSheets.length
    : asNum(scan?.sheets_total);
  const hiddenSheets = factSheets.filter(
    (s) => asStr(s.state) === "hidden",
  );
  const hiddenCount = useFacts ? hiddenSheets.length : asNum(scan?.sheets_hidden);
  const formulaCells = useFacts
    ? factSheets.reduce((acc, s) => acc + (asNum(s.formulas) ?? 0), 0)
    : asNum(scan?.formula_cells);
  const errorsByType =
    asDict(useFacts ? facts?.errors_by_type : scan?.errors_by_type) ?? {};
  const errorCells = useFacts
    ? Object.values(errorsByType).reduce<number>(
        (acc, v) => acc + (asNum(v) ?? 0),
        0,
      )
    : asNum(scan?.error_cells);
  const externalLinks = useFacts
    ? asNum(facts?.external_links)
    : asNum(scan?.external_links);
  const hardcodes = useFacts
    ? asArray(facts?.hardcode_candidates).length
    : asNum(scan?.hardcode_candidates);
  const hiddenWithErrors = hiddenSheets.filter(
    (s) => (asNum(s.errors) ?? 0) > 0,
  );

  const sections = asArray(report.section_assessments)
    .map(asDict)
    .filter((s): s is Dict => !!s);

  const issues = asArray(report.key_issues)
    .map(asDict)
    .filter((s): s is Dict => !!s)
    .sort(
      (a, b) =>
        (PRIORITY_ORDER[asStr(a.priority) ?? ""] ?? 3) -
        (PRIORITY_ORDER[asStr(b.priority) ?? ""] ?? 3),
    );

  const assumptions = asArray(report.key_assumptions)
    .map(asDict)
    .filter((s): s is Dict => !!s);

  const questions = asArray(report.management_questions)
    .map(asStr)
    .filter((s): s is string => !!s);

  return (
    <div className="model-review-report mx-auto max-w-4xl space-y-5 px-4 py-8">
      <style>{`
        @media print {
          @page {
            margin: 0.75in;
          }
          body.model-review-print {
            background: #fff !important;
            color: #000 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          body.model-review-print header.sticky {
            display: none !important;
          }
          .model-review-report .no-print {
            display: none !important;
          }
          .model-review-report {
            max-width: none !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          .model-review-report > section:first-of-type {
            page-break-inside: avoid;
          }
          .model-review-report section,
          .model-review-report .rounded-lg {
            border: none !important;
            background: transparent !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            padding: 0.5rem 0 !important;
            margin: 1rem 0 !important;
          }
          .model-review-report h1,
          .model-review-report h2,
          .model-review-report h3 {
            color: #000 !important;
            break-after: avoid;
          }
          .model-review-report .font-serif {
            color: #000 !important;
          }
          .model-review-report p,
          .model-review-report li,
          .model-review-report td,
          .model-review-report th {
            color: #000 !important;
          }
          .model-review-report .text-muted-foreground,
          .model-review-report .text-brand-muted {
            color: #333 !important;
          }
          .model-review-report .overflow-x-auto {
            overflow: visible !important;
            width: 100% !important;
          }
          .model-review-report table {
            width: 100% !important;
            min-width: 0 !important;
            border-collapse: collapse !important;
            page-break-inside: auto !important;
          }
          .model-review-report thead {
            display: table-row-group !important;
          }
          .model-review-report tr {
            border-top: 1px solid #ccc !important;
            border-bottom: 1px solid #ccc !important;
            page-break-inside: auto !important;
          }
          .model-review-report td,
          .model-review-report th {
            padding: 0.35rem 0.5rem 0.35rem 0 !important;
            vertical-align: top !important;
            border: none !important;
          }
          .model-review-report .bg-amber-50,
          .model-review-report .bg-amber-100,
          .model-review-report .bg-emerald-100,
          .model-review-report .bg-red-100,
          .model-review-report .bg-muted {
            background: transparent !important;
          }
          .model-review-report .border-amber-300 {
            border: 1px solid #999 !important;
          }
          .model-review-report .border-brand-line {
            border-color: #ccc !important;
          }
        }
      `}</style>

      <div className="no-print space-y-6">
        <DealPageHeader dealId={dealId} />
        <DealNav dealId={dealId} active="reports" />
      </div>
      <div className="flex items-start justify-between gap-4">
        <h1 className="font-serif text-2xl text-brand-ink">{pageTitle}</h1>
        <div className="flex items-center gap-3 no-print">
          <button
            type="button"
            onClick={handleDownloadPdf}
            className="shrink-0 text-sm underline underline-offset-2"
          >
            Download PDF
          </button>
          <Link
            to="/deals/$dealId"
            params={{ dealId }}
            className="shrink-0 text-sm underline underline-offset-2"
          >
            Back to the deal
          </Link>
        </div>
      </div>

      {/* 1. Headline */}
      <section className="rounded-lg border border-brand-line bg-brand-paper p-5">
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <span className="font-serif text-4xl text-brand-ink">
            {score != null ? `${score} of 10` : "Score not available"}
          </span>
          {recommendation && (
            <span className="text-base text-brand-ink">{recommendation}</span>
          )}
        </div>
        {(confidence || confidenceLimiters) && (
          <p className="mt-2 text-sm text-muted-foreground">
            {confidence ? `Confidence: ${confidence}.` : ""}
            {confidenceLimiters ? ` ${confidenceLimiters}` : ""}
          </p>
        )}
      </section>

      {/* 2. Mismatch banner */}
      {matchesBranch === false && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <p>
            The uploaded model does not appear to match this deal's type. The
            review covers what is actually in the file.
          </p>
          {modelTypeDetected && (
            <p className="mt-1">Model type detected: {modelTypeDetected}</p>
          )}
          {language && language.toLowerCase() !== "english" && (
            <p className="mt-1">Model language: {language}</p>
          )}
        </div>
      )}

      {/* 3. Top observations */}
      {(observations.length > 0 || modelingRisk || businessRisk) && (
        <Section title="Top observations">
          {observations.length > 0 && (
            <ol className="list-decimal space-y-2 pl-5 text-sm text-brand-ink">
              {observations.map((o, i) => (
                <li key={i}>{o}</li>
              ))}
            </ol>
          )}
          {modelingRisk && (
            <p className="mt-4 text-sm text-brand-ink">
              <span className="font-medium">Biggest modeling risk: </span>
              {modelingRisk}
            </p>
          )}
          {businessRisk && (
            <p className="mt-2 text-sm text-brand-ink">
              <span className="font-medium">Biggest business risk: </span>
              {businessRisk}
            </p>
          )}
        </Section>
      )}

      {/* 4. Workbook facts */}
      {(sheetsTotal != null ||
        formulaCells != null ||
        errorCells != null ||
        externalLinks != null ||
        hardcodes != null) && (
        <Section title="Workbook facts">
          <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
            {sheetsTotal != null && (
              <div>
                <dt className="text-xs text-muted-foreground">Sheets</dt>
                <dd className="tabular-nums text-brand-ink">{sheetsTotal}</dd>
              </div>
            )}
            {hiddenCount != null && (
              <div>
                <dt className="text-xs text-muted-foreground">Hidden sheets</dt>
                <dd className="tabular-nums text-brand-ink">{hiddenCount}</dd>
              </div>
            )}
            {formulaCells != null && (
              <div>
                <dt className="text-xs text-muted-foreground">Formula cells</dt>
                <dd className="tabular-nums text-brand-ink">{formulaCells}</dd>
              </div>
            )}
            {errorCells != null && (
              <div>
                <dt className="text-xs text-muted-foreground">Error cells</dt>
                <dd className="tabular-nums text-brand-ink">{errorCells}</dd>
              </div>
            )}
            {externalLinks != null && (
              <div>
                <dt className="text-xs text-muted-foreground">External links</dt>
                <dd className="tabular-nums text-brand-ink">{externalLinks}</dd>
              </div>
            )}
            {hardcodes != null && (
              <div>
                <dt className="text-xs text-muted-foreground">
                  Hardcode candidates
                </dt>
                <dd className="tabular-nums text-brand-ink">{hardcodes}</dd>
              </div>
            )}
          </dl>

          {Object.keys(errorsByType).length > 0 && (
            <p className="mt-3 text-sm text-brand-ink">
              Errors by type:{" "}
              {Object.entries(errorsByType)
                .map(([k, v]) => `${k} ${asNum(v) ?? 0}`)
                .join(", ")}
            </p>
          )}

          {hiddenWithErrors.length > 0 && (
            <p className="mt-2 text-sm text-brand-ink">
              Hidden sheets with errors:{" "}
              {hiddenWithErrors
                .map(
                  (s) => `${asStr(s.name) ?? "Unnamed"} (${asNum(s.errors) ?? 0})`,
                )
                .join(", ")}
            </p>
          )}
        </Section>
      )}

      {/* 5. Section assessments */}
      {sections.length > 0 && (
        <Section title="Section assessments">
          <ul className="space-y-3">
            {sections.map((s, i) => {
              const grade = asStr(s.grade) ?? "Not Applicable";
              return (
                <li key={i} className="border-b border-brand-line pb-3 last:border-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-brand-ink">
                      {asStr(s.section) ?? "Section"}
                    </span>
                    <Badge
                      label={grade}
                      className={
                        GRADE_CLASS[grade] ?? "bg-muted text-muted-foreground"
                      }
                    />
                  </div>
                  {asStr(s.summary) && (
                    <p className="mt-1 text-sm text-muted-foreground">
                      {asStr(s.summary)}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        </Section>
      )}

      {/* 6. Key issues */}
      {issues.length > 0 && (
        <Section title="Key issues">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground">
                  <th className="pb-2 pr-3 font-medium">Priority</th>
                  <th className="pb-2 pr-3 font-medium">Worksheet</th>
                  <th className="pb-2 pr-3 font-medium">Cell or section</th>
                  <th className="pb-2 pr-3 font-medium">Observation</th>
                  <th className="pb-2 pr-3 font-medium">Impact</th>
                  <th className="pb-2 font-medium">Recommendation</th>
                </tr>
              </thead>
              <tbody>
                {issues.map((it, i) => (
                  <tr key={i} className="border-t border-brand-line align-top">
                    <td className="py-2 pr-3">{asStr(it.priority) ?? ""}</td>
                    <td className="py-2 pr-3">{asStr(it.worksheet) ?? ""}</td>
                    <td className="py-2 pr-3">{asStr(it.cell_or_section) ?? ""}</td>
                    <td className="py-2 pr-3">{asStr(it.observation) ?? ""}</td>
                    <td className="py-2 pr-3">{asStr(it.impact) ?? ""}</td>
                    <td className="py-2">{asStr(it.recommendation) ?? ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* 7. Key assumptions */}
      {assumptions.length > 0 && (
        <Section title="Key assumptions">
          <ul className="space-y-3">
            {assumptions.map((a, i) => {
              const cls = asStr(a.classification) ?? "";
              return (
                <li key={i}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-brand-ink">
                      {asStr(a.assumption) ?? ""}
                    </span>
                    {cls && (
                      <Badge
                        label={cls}
                        className={
                          CLASSIFICATION_CLASS[cls] ??
                          "bg-muted text-muted-foreground"
                        }
                      />
                    )}
                  </div>
                  {asStr(a.note) && (
                    <p className="mt-1 text-sm text-muted-foreground">
                      {asStr(a.note)}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        </Section>
      )}

      {/* 8. Management questions */}
      {questions.length > 0 && (
        <Section title="Questions investors will ask">
          <ol className="list-decimal space-y-2 pl-5 text-sm text-brand-ink">
            {questions.map((q, i) => (
              <li key={i}>{q}</li>
            ))}
          </ol>
        </Section>
      )}
    </div>
  );
}
